import {noop} from '@github-ui/noop'
import {clsx} from 'clsx'
import type {FocusEvent, KeyboardEvent, PropsWithChildren, ReactElement} from 'react'
import {Fragment, useCallback, useEffect, useId, useMemo, useRef} from 'react'

import {useListViewId} from '../ListView/IdContext'
import {useListViewItems} from '../ListView/ItemsContext'
import {useListViewSelection} from '../ListView/SelectionContext'
import {useListViewVariant} from '../ListView/VariantContext'
import type {PrefixedStylableProps, StylableProps} from '../types'
import {ListItemActionBar} from './ActionBar'
import {ActionsProvider} from './ActionsContext'
import {AlignmentProvider, type AlignmentType, useListItemAlignment} from './AlignmentContext'
import {DescriptionProvider, useListItemDescription} from './DescriptionContext'
import styles from './ListItem.module.css'
import {ListItemMetadataContainer, type ListItemMetadataContainerProps} from './MetadataContainer'
import {NewActivityProvider, useListItemNewActivity} from './NewActivityContext'
import {SelectionProvider, useListItemSelection} from './SelectionContext'
import {StatusProvider, useListItemStatus} from './StatusContext'
import type {ListItemTitle} from './Title'
import {TitleProvider, useListItemTitle} from './TitleContext'

export type ListItemProps = StylableProps &
  PrefixedStylableProps<'metadataContainer'> &
  PropsWithChildren<{
    ref?: React.Ref<HTMLLIElement>
    isSelected?: boolean
    isActive?: boolean
    onSelect?: (isSelected: boolean) => void
    /*
     * Optional. Overrides the default behavior of the list item when a key is pressed
     */
    onKeyDown?: (event: KeyboardEvent<HTMLLIElement>) => void
    onFocus?: (event: FocusEvent<HTMLLIElement>) => void

    /**
     * A ListItem title communicates the overall purpose of the ListItem.
     */
    title: ReactElement<typeof ListItemTitle>

    /**
     * Optional extra elements to display on the right side of the list item. You can optionally wrap individual
     * pieces of content in `ListItem.Metadata` for a consistent appearance.
     */
    metadata?: ListItemMetadataContainerProps['children']

    /**
     * Optional menu of additional actions to be shown on the right side of the ListItem. Use `ListItem.ActionBar`.
     */
    secondaryActions?: ReactElement<typeof ListItemActionBar>
    /*
     * Optional. Accessibility role assigned to <li> when not a direct child of a list
     */
    role?: string
    /**
     * Component type to render for drag and drop container
     */
    as?: React.ElementType
    /*
     * Align the leading visual and selection checkbox to the top of the first line of text or center it vertically
     * Default: top
     */
    alignment?: AlignmentType
  }>

const ListItemBase = ({
  ref: forwardedRef,
  children,
  isActive = false,
  title,
  metadata,
  secondaryActions,
  style,
  className,
  metadataContainerStyle,
  metadataContainerClassName,
  as,
  ...props
}: Omit<ListItemProps, 'isSelected' | 'onSelect'>) => {
  const {idPrefix} = useListViewId()
  const {isSelectable} = useListViewSelection()
  const {variant} = useListViewVariant()
  const {anyItemsWithActionBar, hasResizableActionsWithActionBar} = useListViewItems()
  const uniqueIdSuffix = useId()
  const {isSelected, onSelect} = useListItemSelection()
  const {status: labelStatus} = useListItemStatus()
  const {title: labelTitle, titleAction, headingRef} = useListItemTitle()
  const {description: labelDescription} = useListItemDescription()
  const {hasNewActivity} = useListItemNewActivity()
  const {alignment} = useListItemAlignment()
  const itemRef = useRef<HTMLLIElement>(null)

  // Merge internal ref with forwarded ref
  useEffect(() => {
    if (typeof forwardedRef === 'function') {
      forwardedRef(itemRef.current)
    } else if (forwardedRef) {
      forwardedRef.current = itemRef.current
    }
  }, [forwardedRef])

  useEffect(() => {
    if (!itemRef.current) return
    // Focus the element unless another element in the document is specifically focused
    if (isActive && document.activeElement?.tagName === 'BODY') {
      itemRef.current.focus()
    }
  })

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLLIElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      switch (e.key) {
        case 'Enter':
          if (titleAction) {
            // Only trigger title action if focus is on the list item itself or the title element
            // This prevents triggering when focus is on a child element like a button
            const activeElement = document.activeElement
            const isListItemFocused = itemRef.current === activeElement
            const isTitleFocused =
              headingRef.current && (headingRef.current === activeElement || headingRef.current.contains(activeElement))

            if (isListItemFocused || isTitleFocused) {
              titleAction(e)
            }
          }
          break
        case ' ':
          // When the user presses Space
          if (!isSelectable) break
          if (itemRef?.current !== document.activeElement) break // if the focus is inside the listitem, do not select the item
          e.preventDefault()
          onSelect(!isSelected)
          break
        case 'Escape':
          itemRef?.current?.focus()
          break
        default:
          break
      }
    },
    [titleAction, isSelectable, onSelect, isSelected, headingRef],
  )

  const getAriaLabel = useCallback(() => {
    const labelSelected = isSelected ? 'Selected' : ''
    const labelNewActivity = hasNewActivity ? 'New activity' : ''
    const hasSecondaryActions = !!secondaryActions
    const hasMetadata = (Array.isArray(metadata) && metadata.length > 0) || (metadata && !Array.isArray(metadata))
    let labelAdditionalInfo = ''
    if (hasMetadata || hasSecondaryActions) {
      labelAdditionalInfo = 'More information available below'
    }

    let main = [labelTitle, labelStatus].filter(str => str.trim()).join(': ')
    // Remove additional periods
    try {
      // Check if browser supports negative look-behind

      main = main.replace(/(?<!\.)\.+$/, '')
    } catch {
      while (main.endsWith('.')) {
        main = main.substring(0, main.length - 1)
      }
    }

    const ariaLabel = [labelSelected, main, labelDescription, labelNewActivity, labelAdditionalInfo]
      .filter(str => str.trim())
      .join('. ')
    return ariaLabel.endsWith('.') ? ariaLabel : `${ariaLabel}.`
  }, [secondaryActions, metadata, isSelected, hasNewActivity, labelTitle, labelStatus, labelDescription])

  const As = as ? as : 'li'
  return (
    <As
      ref={itemRef}
      id={`${idPrefix}-list-view-node-${uniqueIdSuffix}`}
      className={clsx(
        styles.listItem,
        isSelected && styles.selected,
        variant === 'compact' && styles.compact,
        alignment === 'center' && styles.centerAligned,
        anyItemsWithActionBar && hasResizableActionsWithActionBar && styles.hasActionBar,
        className,
      )}
      // Handled by useFocusZone with roving tabIndex
      tabIndex={-1}
      aria-label={getAriaLabel()}
      style={style}
      onKeyDown={onKeyDown}
      {...props}
    >
      {title}
      {children}
      {Array.isArray(metadata) && metadata.length > 0 ? (
        <ListItemMetadataContainer style={metadataContainerStyle} className={metadataContainerClassName}>
          {metadata.map((metadataItem, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <Fragment key={index}>{metadataItem}</Fragment>
          ))}
        </ListItemMetadataContainer>
      ) : (
        Boolean(metadata && !Array.isArray(metadata)) && (
          <ListItemMetadataContainer style={metadataContainerStyle} className={metadataContainerClassName}>
            {metadata}
          </ListItemMetadataContainer>
        )
      )}
      {secondaryActions ?? (anyItemsWithActionBar && <ListItemActionBar />)}
    </As>
  )
}

ListItemBase.displayName = 'ListItemBase'

export const ListItem = ({ref, children, isSelected = false, onSelect = noop, alignment, ...rest}: ListItemProps) => {
  const {setSelectedCount} = useListViewSelection()
  const isSelectedRef = useRef(isSelected)

  useEffect(() => {
    isSelectedRef.current = isSelected
  })

  useEffect(() => {
    setSelectedCount(count => (isSelected ? count + 1 : Math.max(0, count - 1)))
  }, [isSelected, setSelectedCount])

  useEffect(() => {
    return () => {
      if (isSelectedRef.current) {
        setSelectedCount(count => Math.max(0, count - 1))
      }
    }
  }, [setSelectedCount])

  const selectionProviderProps = useMemo(() => ({isSelected, onSelect}), [isSelected, onSelect])

  return (
    <ActionsProvider>
      <TitleProvider>
        <SelectionProvider value={selectionProviderProps}>
          <NewActivityProvider>
            <DescriptionProvider>
              <StatusProvider>
                <AlignmentProvider alignment={alignment}>
                  <ListItemBase {...rest} ref={ref}>
                    {children}
                  </ListItemBase>
                </AlignmentProvider>
              </StatusProvider>
            </DescriptionProvider>
          </NewActivityProvider>
        </SelectionProvider>
      </TitleProvider>
    </ActionsProvider>
  )
}

ListItem.displayName = 'ListItem'
